import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent } from 'react';
import { FormControl, DropdownButton, MenuItem, Glyphicon, InputGroup, Well, Button, Label } from 'react-bootstrap';
import { IUser, ICenterBase, IListItem, ITicketGroup, searchByList } from '@nkr/models/index';
import { FilterTableWrapper } from './styles';
import { IFilter } from './filter';
import MaskedInput from 'react-maskedinput';
import filterChangeTypes from './filter-change-types';

interface IProps {
    isSuper: boolean,
    isTicketingOnly: boolean,
    userId: number,
    assignedTo: number[],
    filter: IFilter,
    filterChanged: boolean,
    allOpen: boolean,
    userList: IUser[],
    centerList: ICenterBase[],
    ticketTypeList: IListItem[],
    ticketStatusList: IListItem[],
    ticketTierList: IListItem[],
    ticketGroupList: ITicketGroup[],
    ticketUrgencyList: IListItem[],
    onFilterChange: (property: string, value: any) => void,
    onFilter: () => void,
    onExport: () => void,
    onResetFilters: (excludeStatus: boolean) => void,
    onRestoreDefaults: () => void
}

export const FilterDrawer = (props: IProps) => {
    const fullStatusList =
        props.ticketStatusList.map(x => {
            return { value: x.id, label: x.description, isChecked: props.filter.status.indexOf(x.id) >= 0 };
        });

    const statusLabel = () => {
        if (props.ticketStatusList.length > 0 && props.filter.status.length > 0) {
            let sortedList = _.sortBy(props.filter.status, s => s);

            return sortedList.map(id => {
                let f = props.ticketStatusList.find(x => x.id == id);

                if (f != null) {
                    return f.description;
                }
            }).join(', ');
        } else {
            return '- Show All -';
        }
    };

    const searchByLabel = () => {
        let sb = searchByList.find(x => x.id == props.filter.searchBy);
        return sb.description;
    };

    const handleNumberChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        if (e.currentTarget.name == 'type') {
            props.onFilterChange(filterChangeTypes.TYPE, parseInt(e.currentTarget.value));
        }

        if (e.currentTarget.name == 'group') {
            props.onFilterChange(filterChangeTypes.GROUP, parseInt(e.currentTarget.value));
        }

        if (e.currentTarget.name == 'center') {
            props.onFilterChange(filterChangeTypes.CENTER, parseInt(e.currentTarget.value));
        }

        if (e.currentTarget.name == 'tier') {
            props.onFilterChange(filterChangeTypes.TIER, parseInt(e.currentTarget.value));
        }
    };

    const handleStatusChange = (status: number) => {
        let list = [...props.filter.status];

        if (list.indexOf(status) >= 0) {
            list = list.filter(s => s != status);
        } else {
            list.push(status);
        }

        props.onFilterChange(filterChangeTypes.STATUS, list);
    };

    const handleSearchByChange = (searchBy: any) => {
        props.onFilterChange(filterChangeTypes.SEARCH_BY, searchBy);
    };

    const handleTextChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        if (e.currentTarget.name == 'search') {
            props.onFilterChange(filterChangeTypes.SEARCH, e.currentTarget.value);
        }

        if (e.currentTarget.name == 'startDate') {
            props.onFilterChange(filterChangeTypes.START_DATE, e.currentTarget.value);
        }

        if (e.currentTarget.name == 'endDate') {
            props.onFilterChange(filterChangeTypes.END_DATE, e.currentTarget.value);
        }
    };

    const handleSearch = () => {
        props.onFilter();
    }

    const ticketGroupList = _.sortBy(props.ticketGroupList.filter(x => x.category != 20), item => item.name);

    const startDate = moment(props.filter.startDate, 'MM/DD/YYYY');
    const endDate = moment(props.filter.endDate, 'MM/DD/YYYY');

    const isValidStartDate = startDate.isValid();
    const isValidEndDate = endDate.isValid();

    const diffInMonths = endDate.diff(startDate, 'month');

    const disableSearch = props.filter.status.length == 0 || !isValidStartDate || !isValidEndDate || diffInMonths > 6;

    return (
        <Well bsSize='sm'>
            <FilterTableWrapper condensed>
                <tbody>
                    <tr>
                        <td>Request:</td>
                        <td>
                            <FormControl
                                componentClass='select'
                                bsSize='sm'
                                name='type'
                                style={{ width: 150 }}
                                value={props.filter.type}
                                onChange={handleNumberChange}
                            >
                                <option value={-1}>- ANY TYPE -</option>
                                {props.ticketTypeList.map(tt =>
                                    <option key={`t-type-${tt.id}`} value={tt.id}>{tt.description}</option>
                                )}
                            </FormControl>
                        </td>

                        <td>Group:</td>
                        <td>
                            <FormControl
                                componentClass='select'
                                bsSize='sm'
                                name='group'
                                placeholder='select group'
                                style={{ width: 'initial' }}
                                value={props.filter.group}
                                onChange={handleNumberChange}
                            >
                                <option value='0'>- ANY GROUP -</option>
                                {ticketGroupList.map((group, index) =>
                                    <option key={'group-' + index} value={group.id}>{group.name}</option>
                                )}
                            </FormControl>
                        </td>

                        <td>Center:</td>
                        <td>
                            <FormControl
                                componentClass='select'
                                bsSize='sm'
                                name='center'
                                placeholder='select center'
                                style={{ width: 'initial' }}
                                value={props.filter.center}
                                onChange={handleNumberChange}
                            >
                                <option value='0'>- ANY CENTER -</option>
                                <optgroup label='Member Centers'>
                                    {props.centerList.filter(x => x.isMemberCenter).map(center =>
                                        <option key={'mc-' + center.id} value={center.id}>{center.alias}</option>
                                    )}
                                </optgroup>

                                <optgroup label='Non-member Centers'>
                                    {props.centerList.filter(x => !x.isMemberCenter).map(center =>
                                        <option key={'pc-' + center.id} value={center.id}>{center.alias}</option>
                                    )}
                                </optgroup>
                            </FormControl>
                        </td>

                        <td>Status:</td>
                        <td>
                            <DropdownButton disabled={props.allOpen} bsSize='sm' id='groups-dropdown' title={statusLabel()}>
                                <MenuItem><label style={{ marginBottom: 0, fontWeight: 400 }}>- Select -</label></MenuItem>
                                {fullStatusList.map(item =>
                                    <MenuItem key={'status-' + item.value}>
                                        <label onClick={() => handleStatusChange(item.value)} style={{ marginBottom: 0, fontWeight: 400 }}>
                                            <Glyphicon glyph={item.isChecked ? 'check' : 'unchecked'} style={{ marginRight: 10 }} />
                                            {item.label}
                                        </label>
                                    </MenuItem>
                                )}
                            </DropdownButton>
                        </td>

                        {!props.isTicketingOnly ?
                            <>
                                <td>Tier:</td>
                                <td>
                                    <FormControl
                                        componentClass='select'
                                        bsSize='sm'
                                        name='tier'
                                        style={{ width: 'initial' }}
                                        value={props.filter.tier}
                                        onChange={handleNumberChange}
                                    >
                                        <option value={-1}>- ANY TIER -</option>
                                        {props.ticketTierList.map(tt =>
                                            <option key={`t-tier-${tt.id}`} value={tt.id}>{tt.description}</option>
                                        )}
                                    </FormControl>
                                </td>
                            </>
                            :
                            <td colSpan={2}>&nbsp;</td>
                        }
                    </tr>

                    <tr>
                        <td>Search by:</td>
                        <td>
                            <InputGroup>
                                <InputGroup.Button>
                                    <DropdownButton bsSize='sm' title={searchByLabel()} id='search-by' onSelect={handleSearchByChange}>
                                        {searchByList.map((sb, index) =>
                                            <MenuItem key={`sb-${index}`} eventKey={sb.id}>{sb.description}</MenuItem>
                                        )}
                                    </DropdownButton>
                                </InputGroup.Button>
                                <FormControl
                                    bsSize='sm'
                                    name='search'
                                    placeholder='Search ticket #, names, email addresses, messages...'
                                    value={props.filter.search}
                                    onChange={handleTextChange}
                                />
                            </InputGroup>
                        </td>

                        {!props.allOpen && (
                            <>
                                <td>Date Range:</td>
                                <td>
                                    <InputGroup bsSize='sm'>
                                        <MaskedInput
                                            className='form-control input-sm'
                                            style={{ borderLeft: '0 none' }}
                                            mask='11/11/1111'
                                            name='startDate'
                                            value={props.filter.startDate}
                                            onChange={handleTextChange}
                                        />
                                        <InputGroup.Addon>to</InputGroup.Addon>
                                        <MaskedInput
                                            className='form-control input-sm'
                                            style={{ borderLeft: '0 none' }}
                                            mask='11/11/1111'
                                            name='endDate'
                                            value={props.filter.endDate}
                                            onChange={handleTextChange}
                                        />
                                    </InputGroup>
                                </td>
                            </>
                        )}

                        <td>&nbsp;</td>

                        <td>
                            <div style={{ display: 'flex' }}>
                                <div style={{ marginRight: 5 }}>
                                    <Button bsStyle='primary' bsSize='sm' disabled={disableSearch} title={disableSearch ? 'Please specify at least one status and the date range is 6 months or less' : ''} onClick={handleSearch}>Search</Button>
                                </div>

                                <div style={{ marginRight: 5 }}>
                                    <DropdownButton bsSize='sm' title='Reset Actions' id='reset-actions'>
                                        <MenuItem onClick={() => props.onResetFilters(false)}>Reset</MenuItem>
                                        <MenuItem onClick={() => props.onResetFilters(true)}>Reset (Keep Status)</MenuItem>
                                        <MenuItem onClick={props.onRestoreDefaults}>Restore Defaults</MenuItem>
                                    </DropdownButton>
                                </div>

                                <Button bsStyle='info' bsSize='sm' disabled={disableSearch} title={disableSearch ? 'At least one status must be selected to be able to search' : ''} onClick={props.onExport}>Export</Button>
                            </div>
                        </td>


                        {props.filterChanged && (
                            <td colSpan={4}>
                                <Label>Filter options have changed, please click Search</Label>
                            </td>
                        )}
                    </tr>
                </tbody>
            </FilterTableWrapper>
        </Well>
    );
}
